import React, { useEffect, useState } from "react";

import Select from "../ReusableComponents/Select";
import { fetchLatestForecastS3Key } from "../../utils/composedForecastUtils";
import { fetchAndParseForecastRecord, getLabelledVariationOptions } from "../../utils/modelOutputUtils";
import { pickModelKey } from "../../utils/seriesMetaData";

interface ModelOutputVariablePickerProps {
  /** The model output whose variables are offered. */
  outputId?: string;
  /** The saved choice; unset means the output's first variable. */
  value?: string;
  onChange: (variation: string) => void;
  /** The variable actually in use (the saved choice, else the first), so the row's preview and channels follow it. */
  onResolved?: (variation: string | undefined) => void;
}

/** Which variable of a multi-variable model output (e.g. temperature_2m, precipitation_surface) a source reads.
 *  Shown only when the output has more than one. Read from the latest forecast file, so "first" is the same
 *  variable the chart falls back to. */
const ModelOutputVariablePicker: React.FC<ModelOutputVariablePickerProps> = ({ outputId, value, onChange, onResolved }) => {
  const [options, setOptions] = useState<Array<{ key: string; label: string }>>([]);

  useEffect(() => {
    setOptions([]);
    if (!outputId) return;
    let cancelled = false;
    fetchLatestForecastS3Key(outputId)
      .then(fetchAndParseForecastRecord)
      .then(({ parsedData }) => {
        if (!cancelled) setOptions(getLabelledVariationOptions(parsedData));
      })
      .catch((err) => console.error("Could not read the output's variables:", err));
    return () => {
      cancelled = true;
    };
  }, [outputId]);

  const keyed = Object.fromEntries(options.map((o) => [o.key, o]));
  const selected = pickModelKey(keyed, value);

  useEffect(() => {
    onResolved?.(selected || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  if (options.length < 2) return null;

  const missing = !!value && selected.toLowerCase() !== value.toLowerCase();

  return (
    <div className="mt-3">
      <Select
        label="Variable"
        value={selected}
        onChange={(e) => onChange(e.target.value)}
        options={options.map((o) => ({ value: o.key, label: o.label }))}
        containerClassName="w-full"
        hint={
          missing
            ? `"${value}" is no longer in this output, so the first variable is shown.`
            : "This output holds several variables; the view draws and scores the one picked here."
        }
      />
    </div>
  );
};

export default ModelOutputVariablePicker;
